import { useState } from "react";
import { createUseStyles } from "react-jss";
import {
  EuiButtonEmpty,
  EuiCopy,
  EuiInMemoryTable,
  EuiLink,
  EuiIcon,
  Criteria,
  CriteriaWithPagination,
} from "@elastic/eui";
import Popover from "./Popover";
import ExportIcon from "components/Icons/ExportIcon";
import { exportFDDataTable, generateExcelFileName, capitalizeFirstLetter } from "utils";

const useStyles = createUseStyles({
  listAnchor: {
    fontWeight: "normal !important",
    whiteSpace: "normal",
    wordWrap: "break-word",
    wordBreak: "break-all",
    overflowWrap: "break-word",
    padding: "6px",
    "&:hover": {
      backgroundColor: "var(--hover-background)",
      borderRadius: "6px",
    },
  },
  linkToList: {
    "& > div:first-child": {
      borderRadius: "6px !important",
    },
    "& thead": {
      display: "none",
    },
    "& .euiTableCellContent": {
      justifyContent: "flex-start",
    },
  },
  linkToListShowHeaders: {
    "& thead": {
      display: "table-header-group",
      backgroundColor: "var(--border-color)",
      "& .euiTableHeaderCell": {
        borderBottom: "1px solid var(--border-color)",
      },
      "& .euiTableHeaderCell .euiTableCellContent__text": {
        fontWeight: 600,
      },
    },
    "& .euiTableRow:first-child .euiTableRowCell": {
      borderTopWidth: "1px !important",
    },
  },
  betweenButton: {
    position: "relative",
    top: "-12px",
    left: "-6px",
  },
  betweenButtonBottom: {
    position: "relative",
    left: "-6px",
    paddingTop: "8px",
  },
  belowButtonContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "8px 0 0 1px",
    marginTop: "4px",
  },
  belowButtonContainerPagination: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    padding: "8px 0 0 1px",
    marginTop: "4px",
    position: "relative",
    zIndex: 1,
  },
  belowButton: {
    padding: "0",
  },
  popoverPanel: {
    zIndex: 3,

    "& .euiSpacer": {
      blockSize: "10px !important",
      height: "10px !important",
      minHeight: "10px !important",
    },
  },
  responsiveTable: {
    width: "100%",
    "& .euiTable": {
      tableLayout: "auto",
      width: "100%",
    },
  },
  responsiveColumn: {
    minWidth: "120px",
    maxWidth: "none",
    width: "auto !important",
    "& .euiTableCellContent": {
      overflow: "hidden !important",
      textOverflow: "ellipsis !important",
      whiteSpace: "nowrap !important",
      maxWidth: "100%",
      display: "block",
    },
    "& .euiTableCellContent__text": {
      wordBreak: "normal",
    },
  },
  iconColumn: {
    width: "25px !important",
    minWidth: "25px !important",
    maxWidth: "25px !important",
  },
  copyColumn: {
    width: "50px !important",
    minWidth: "50px !important",
    maxWidth: "50px !important",
  },
  tableContainer: {
    backgroundColor: "var(--card-background)",
    maxWidth: "100%",
    overflowX: "auto",
    padding: "1px", // Add slight padding to prevent border clipping
    "& .euiTable": {
      borderRadius: "6px !important",
      border: "0.5px solid var(--border-color) !important",
      borderCollapse: "separate !important",
      overflow: "hidden !important",
      borderSpacing: "0 !important",
    },

    "& .euiTableRowCell": {
      borderLeft: "none !important",
      borderRight: "none !important",
      borderBottom: "0.5px solid var(--border-color) !important",
      borderTop: "none !important",
    },

    "& .euiTableRow:last-child .euiTableRowCell": {
      borderBottomWidth: "0px !important",
    },

    "& .euiTableHeaderCell-isSorted": {
      backgroundColor: "var(--border-color) !important",
    },

    "&:before": {
      display: "none !important",
    },

    "& .euiFormControlLayout__childrenWrapper input[type='search']": {
      borderRadius: "6px !important",
    },
  },
});

interface LinkToListProps {
  Items: any;
  Fields?: string[];
  Columns?: any;
  PopOverMaxWidth?: number;
  ButtonProps?: any;
  ButtonTitle?: any;
  Copy?: boolean | null | undefined;
  ShowFirstField?: string;
  SingleItemHasAnchor?: boolean;
  ButtonSize?: string;
  Color?: string;
  MaxLen?: number;
  EmptyValue?: number;
  Icon?: string | null;
  betweenButton?: { name: string; onClick: () => void } | null;
  childrenBetween?: any;
  isSortable?: boolean;
  showHeaders?: boolean;
  showExport?: boolean;
  exportFileName?: string;
  currentUser?: any;
  belowButton?: { name: string; onClick: () => void } | null;
  enableSearch?: boolean;
}

const LinkToList = ({
  Items,
  Fields,
  Columns,
  PopOverMaxWidth = 500,
  ButtonProps = {},
  ButtonTitle,
  Copy = null,
  ShowFirstField = "",
  SingleItemHasAnchor = false,
  ButtonSize = "xs",
  Color = "primary",
  MaxLen = 20,
  EmptyValue = 0,
  Icon,
  betweenButton = null,
  childrenBetween = null,
  isSortable = false,
  showHeaders = false,
  showExport = false,
  exportFileName,
  currentUser,
  belowButton = null,
  enableSearch = true,
}: LinkToListProps) => {
  const classes = useStyles();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(5);
  const [sortField, setSortField] = useState<string>("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const color: any = Color;
  const size: any = ButtonSize;
  const icon: string | null = Icon || null;

  const onTableChange = (criteria: Criteria<any> | CriteriaWithPagination<any>) => {
    // Handle pagination
    if (criteria.page) {
      const { index, size } = criteria.page;
      if (typeof index === "number") setPageIndex(index);
      if (typeof size === "number") setPageSize(size);
    }

    // Handle sorting
    if (criteria.sort) {
      const { field, direction } = criteria.sort;
      if (typeof field === "string") setSortField(field);
      if (direction === "asc" || "desc") setSortDirection(direction);
    }
  };

  const onButtonClick = (e: any) => {
    e.stopPropagation();
    e.preventDefault();
    setIsPopoverOpen(!isPopoverOpen);
  };

  const closePopover = () => setIsPopoverOpen(false);

  const handleExport = () => {
    // Prepare columns for export
    const exportColumns = tableColumns.map((column: any) => ({
      field: column.field,
      id: column.field,
      name: column.name || column.field,
      render: column.render,
      dataType: column.dataType,
      format: column.format,
    }));

    const filename = exportFileName || generateExcelFileName("Data Export");

    exportFDDataTable(
      [
        {
          columns: exportColumns,
          data: Items,
          sheetName: "Data",
        },
      ],
      currentUser,
      filename
    );
  };

  let buttonTitle;
  if (ShowFirstField && ShowFirstField.length > 0 && Items.length > 0) {
    const label =
      MaxLen && Items[0][ShowFirstField]?.length > MaxLen
        ? `${Items[0][ShowFirstField].substring(0, MaxLen)}...`
        : Items[0][ShowFirstField];
    buttonTitle =
      Items.length > 1 ? (
        <>
          <span className="eui-textTruncate txt-color-dark-gray">{label || EmptyValue}</span>
          <span>&nbsp;&nbsp;{`(+${Items.length - 1})`}</span>
        </>
      ) : (
        <span className="txt-color-dark-gray eui-textTruncate">{label || EmptyValue}</span>
      );
  } else if (ButtonTitle) {
    buttonTitle = ButtonTitle;
  } else {
    buttonTitle = Items?.length;
  }

  const button = (
    <EuiLink
      className={classes.listAnchor + " eui-textTruncate"}
      color={color}
      disabled={Items?.length === 0}
      onClick={onButtonClick}
      // @ts-ignore
      size={size}
      {...(ButtonProps || {})}>
      {buttonTitle}
    </EuiLink>
  );

  const tableColumns =
    Fields !== undefined
      ? [
          ...(icon
            ? [
                {
                  name: "",
                  className: classes.iconColumn,
                  render: () => {
                    return <EuiIcon type={icon} />;
                  },
                },
              ]
            : []),
          ...Fields.map((field) => ({
            field,
            name: field ? capitalizeFirstLetter(field) : "",
            sortable: isSortable,
            className: classes.responsiveColumn,
          })),
        ]
      : Columns.map((column: any) => {
          const columnClass = column.width
            ? column.className
            : `${column.className || ""} ${classes.responsiveColumn}`.trim();

          return {
            ...column,
            name: column.name ? capitalizeFirstLetter(column.name) : column.name,
            className: columnClass,
          };
        });

  const copyColumns = Copy
    ? [
        {
          field: Copy,
          name: "",
          className: classes.copyColumn,
          sortable: isSortable,
          render: (field: any, row: any) => {
            const copyText = tableColumns.map((col: any) => row[col.field]).join(" ");
            return (
              <EuiCopy textToCopy={copyText}>
                {(copy) => <EuiButtonEmpty color="text" iconType="copy" onClick={copy} size="xs" />}
              </EuiCopy>
            );
          },
        },
      ]
    : [];

  const tableClasses = `${classes.linkToList} ${
    showHeaders ? classes.linkToListShowHeaders : ""
  } ${classes.responsiveTable}`;

  const handleBelowButtonClick = () => {
    if (belowButton) {
      belowButton.onClick();
      setIsPopoverOpen(false);
    }
  };

  return ShowFirstField &&
    ShowFirstField.length > 0 &&
    Items.length === 1 &&
    !SingleItemHasAnchor ? (
    buttonTitle
  ) : (
    <Popover
      button={button}
      onClose={closePopover}
      isOpen={isPopoverOpen}
      ownFocus
      className={classes.popoverPanel}
      panelPaddingSize={"s"}
      children={
        <>
          <div className={classes.tableContainer}>
            <EuiInMemoryTable
              childrenBetween={
                betweenButton ? (
                  <>
                    <EuiButtonEmpty
                      className={classes.betweenButton}
                      size="xs"
                      color={"primary"}
                      onClick={betweenButton?.onClick}>
                      {betweenButton?.name}
                    </EuiButtonEmpty>
                  </>
                ) : <></> || childrenBetween ? (
                  <>{childrenBetween}</>
                ) : (
                  <></>
                )
              }
              className={tableClasses}
              columns={tableColumns.concat(copyColumns)}
              compressed
              items={Items}
              onChange={onTableChange}
              pagination={
                !Items || Items.length < pageSize
                  ? false
                  : {
                      showPerPageOptions: true,
                      pageSize: pageSize,
                      pageSizeOptions: [5, 10, 15],
                      pageIndex: pageIndex,
                    }
              }
              search={
                enableSearch && Items
                  ? {
                      box: { incremental: true },
                      toolsRight:
                        showExport && Items.length > 0
                          ? [
                              <EuiButtonEmpty
                                key="export-button"
                                size="xs"
                                onClick={handleExport}
                                aria-label="Export data"
                                data-test-subj="link-to-list-export"
                                color="primary">
                                <ExportIcon fill="#0077CC" />
                              </EuiButtonEmpty>,
                            ]
                          : undefined,
                    }
                  : false
              }
              sorting={
                isSortable
                  ? {
                      sort: {
                        field: sortField || (tableColumns.length > 0 ? tableColumns[0].field : ""),
                        direction: sortDirection,
                      },
                    }
                  : false
              }
            />
          </div>

          {belowButton && (
            <div className={classes.belowButtonContainer}>
              <EuiButtonEmpty
                className={classes.belowButton}
                size="xs"
                color="primary"
                onClick={handleBelowButtonClick}>
                {belowButton.name}
              </EuiButtonEmpty>
            </div>
          )}
        </>
      }
    />
  );
};

export default LinkToList;
